import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Users, UserPlus, UserCheck, TrendingUp, Loader2, Search, Inbox, Check, X, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { userAPI } from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SearchBar = ({ value, onChange, placeholder }) => (
  <div className="relative mb-4">
    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
    <Input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="pl-9 rounded-xl h-9 text-sm"
      data-testid="tab-search-input"
    />
  </div>
);

const Connections = () => {
  const [searchParams] = useSearchParams();
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [followRequests, setFollowRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('followers');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const [viewingUser, setViewingUser] = useState(null);

  // Set active tab from URL parameter
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      if (['followers', 'following', 'suggestions', 'requests'].includes(tab)) {
        setActiveTab(tab);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    loadConnections();
  }, [searchParams]); // Reload when URL params change

  // Filter users based on search query
  const filterUsers = (users) => {
    if (!searchQuery.trim()) return users;
    const query = searchQuery.toLowerCase();
    return users.filter(user => 
      user.name?.toLowerCase().includes(query) ||
      user.username?.toLowerCase().includes(query) ||
      user.persona?.toLowerCase().includes(query) ||
      user.sports?.some(sport => sport.toLowerCase().includes(query))
    );
  };

  const loadConnections = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const currentUserId = currentUser.userId || currentUser._id;
      
      // Get userId from URL params, default to current user
      const userIdParam = searchParams.get('userId');
      const userId = userIdParam || currentUserId;

      if (!userId) {
        toast.error('Please login to view connections');
        return;
      }

      // Fetch user info if viewing someone else's connections
      if (userIdParam && userIdParam !== currentUserId) {
        try {
          const userResponse = await axios.get(
            `${BACKEND_URL}/api/users/${userId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setViewingUser(userResponse.data);
        } catch (error) {
          console.error('Error loading user info:', error);
        }
      } else {
        setViewingUser(null); // Viewing own connections
      }

      let followersData = [];
      let followingData = [];

      // Use the dedicated backend endpoints
      try {
        // Get followers
        const followersResponse = await axios.get(
          `${BACKEND_URL}/api/users/${userId}/followers`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        followersData = followersResponse.data || [];
        setFollowers(followersData);
        console.log('Followers loaded:', followersData.length);
      } catch (error) {
        console.error('Error loading followers:', error);
        setFollowers([]);
      }

      try {
        // Get following
        const followingResponse = await axios.get(
          `${BACKEND_URL}/api/users/${userId}/following`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        followingData = followingResponse.data || [];
        setFollowing(followingData);
        console.log('Following loaded:', followingData.length);
      } catch (error) {
        console.error('Error loading following:', error);
        setFollowing([]);
      }

      // Get suggestions (only for own profile)
      if (!userIdParam || userIdParam === currentUserId) {
        try {
          const suggestionsResponse = await axios.get(
            `${BACKEND_URL}/api/users/suggested`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setSuggestions(suggestionsResponse.data || []);
        } catch (error) {
          console.error('Error loading suggestions:', error);
          setSuggestions([]);
        }
      } else {
        setSuggestions([]); // Don't show suggestions for other users
      }

      // Load follow requests (only for own connections)
      if (!userIdParam || userIdParam === currentUserId) {
        try {
          const reqResponse = await userAPI.getReceivedFollowRequests();
          setFollowRequests(reqResponse.data || []);
        } catch (error) {
          console.error('Error loading follow requests:', error);
          setFollowRequests([]);
        }
      }

    } catch (error) {
      console.error('Error loading connections:', error);
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/users/${userId}/follow`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Followed successfully');
      loadConnections(); // Reload to update lists
    } catch (error) {
      console.error('Error following user:', error);
      toast.error('Failed to follow user');
    }
  };

  const handleUnfollow = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/users/${userId}/unfollow`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Unfollowed successfully');
      loadConnections(); // Reload to update lists
    } catch (error) {
      console.error('Error unfollowing user:', error);
      toast.error('Failed to unfollow user');
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await userAPI.acceptFollowRequest(requestId);
      toast.success('Follow request accepted');
      setFollowRequests(prev => prev.filter(r => r._id !== requestId));
      loadConnections();
    } catch (error) {
      toast.error('Failed to accept request');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await userAPI.rejectFollowRequest(requestId);
      toast.success('Follow request declined');
      setFollowRequests(prev => prev.filter(r => r._id !== requestId));
    } catch (error) {
      toast.error('Failed to decline request');
    }
  };

  const UserCard = ({ user, showFollowButton = false, isFollowing = false }) => (
    <Card className="cursor-pointer hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3" onClick={() => navigate(`/profile/${user.username}`)}>
            <Avatar className="w-12 h-12">
              <AvatarImage src={user.profileImage} />
              <AvatarFallback>{user.name?.[0] || 'U'}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground">{user.name}</h3>
              <p className="text-sm text-muted-foreground">@{user.username}</p>
              <Badge className="mt-1 badge-primary text-[10px]">
                {user.persona}
              </Badge>
            </div>
          </div>
          {showFollowButton && (
            <Button
              size="sm"
              variant={isFollowing ? "outline" : "default"}
              className={!isFollowing ? 'btn-primary' : ''}
              onClick={(e) => {
                e.stopPropagation();
                isFollowing ? handleUnfollow(user._id) : handleFollow(user._id);
              }}
            >
              {isFollowing ? (
                <>
                  <UserCheck className="w-4 h-4 mr-1" />
                  Following
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-1" />
                  Follow
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-0">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 pt-20">
          <div className="py-6" data-testid="connections-skeleton">
            <Skeleton className="h-8 w-44 mb-6" />
            <Skeleton className="h-10 w-full rounded-lg mb-6" />
            <div className="space-y-3">
              {[0, 1, 2, 3, 4].map((i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Skeleton className="w-12 h-12 rounded-full" />
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-28" />
                          <Skeleton className="h-3 w-20" />
                          <Skeleton className="h-5 w-14 rounded-full" />
                        </div>
                      </div>
                      <Skeleton className="h-8 w-24 rounded-md" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 pt-20">
        <div className="py-6 skeleton-fade-in">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-foreground">Connections</h1>
          </div>

          {/* Contextual Header */}
          {viewingUser && (
            <div className="mb-6 p-4 bg-primary/10 dark:bg-primary/10 rounded-lg border border-primary/20 dark:border-primary/30">
              <div className="flex items-center space-x-3">
                <Avatar className="w-10 h-10">
                  <AvatarImage src={viewingUser.profileImage} />
                  <AvatarFallback>{viewingUser.name?.[0] || 'U'}</AvatarFallback>
                </Avatar>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">
                    {viewingUser.name}'s {activeTab === 'followers' ? 'Followers' : activeTab === 'following' ? 'Following' : 'Connections'}
                  </h2>
                  <p className="text-sm text-muted-foreground">@{viewingUser.username}</p>
                </div>
              </div>
            </div>
          )}

          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSearchQuery(''); }} className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-6">
              <TabsTrigger value="followers">
                <Users className="w-4 h-4 mr-2" />
                Followers ({followers.length})
              </TabsTrigger>
              <TabsTrigger value="following">
                <UserCheck className="w-4 h-4 mr-2" />
                Following ({following.length})
              </TabsTrigger>
              <TabsTrigger value="suggestions">
                <TrendingUp className="w-4 h-4 mr-2" />
                Discover
              </TabsTrigger>
              <TabsTrigger value="requests" className="relative">
                <Inbox className="w-4 h-4 mr-2" />
                Requests
                {followRequests.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-destructive text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {followRequests.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="requests">
              <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search requests..." />
              {filterUsers(followRequests.map(r => ({...r.fromUser, _reqId: r._id, _reqMsg: r.message}))).length === 0 && followRequests.length > 0 ? (
                <Card><CardContent className="p-8 text-center"><p className="text-muted-foreground">No requests match your search</p></CardContent></Card>
              ) : followRequests.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Inbox className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-lg font-semibold text-foreground mb-2">No pending requests</p>
                    <p className="text-muted-foreground">When someone requests to follow you, it will appear here</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {followRequests.filter(req => {
                    if (!searchQuery.trim()) return true;
                    const q = searchQuery.toLowerCase();
                    return req.fromUser.name?.toLowerCase().includes(q) || req.fromUser.username?.toLowerCase().includes(q) || req.fromUser.persona?.toLowerCase().includes(q);
                  }).map((req) => (
                    <Card key={req._id} data-testid={`follow-request-${req._id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Avatar className="w-12 h-12 cursor-pointer" onClick={() => navigate(`/profile/${req.fromUser.username}`)}>
                            <AvatarImage src={req.fromUser.profileImage} />
                            <AvatarFallback>{req.fromUser.name?.[0] || 'U'}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-foreground cursor-pointer hover:underline" onClick={() => navigate(`/profile/${req.fromUser.username}`)}>
                                {req.fromUser.name}
                              </h3>
                              <Badge className="badge-primary text-[10px]">{req.fromUser.persona}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">@{req.fromUser.username}</p>
                            {req.message && (
                              <div className="mt-2 p-2 bg-muted/50 rounded-lg text-sm text-foreground flex items-start gap-2">
                                <MessageCircle className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                                <span>{req.message}</span>
                              </div>
                            )}
                            <div className="flex gap-2 mt-3">
                              <Button size="sm" className="btn-primary" onClick={() => handleAcceptRequest(req._id)} data-testid={`accept-request-${req._id}`}>
                                <Check className="w-4 h-4 mr-1" /> Accept
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleRejectRequest(req._id)} data-testid={`reject-request-${req._id}`}>
                                <X className="w-4 h-4 mr-1" /> Decline
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="suggestions">
              <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search people to discover..." />
              {filterUsers(suggestions).length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <TrendingUp className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-lg font-semibold text-foreground mb-2">
                      {suggestions.length === 0 ? 'No suggestions available' : 'No results match your search'}
                    </p>
                    <p className="text-muted-foreground">
                      {suggestions.length === 0 ? 'Check back later for personalized recommendations' : 'Try a different search term'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {filterUsers(suggestions).map((user) => (
                    <UserCard key={user._id} user={user} showFollowButton={true} isFollowing={false} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="followers">
              <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search followers..." />
              {filterUsers(followers).length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-lg font-semibold text-foreground mb-2">
                      {followers.length === 0 ? 'No followers yet' : 'No followers match your search'}
                    </p>
                    <p className="text-muted-foreground">
                      {followers.length === 0 ? 'Share your profile to gain followers' : 'Try a different search term'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {filterUsers(followers).map((user) => (
                    <UserCard key={user._id} user={user} showFollowButton={true} isFollowing={following.some(f => f._id === user._id)} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="following">
              <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search following..." />
              {filterUsers(following).length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <UserCheck className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-lg font-semibold text-foreground mb-2">
                      {following.length === 0 ? 'Not following anyone yet' : 'No users match your search'}
                    </p>
                    <p className="text-muted-foreground">
                      {following.length === 0 ? 'Discover users to follow' : 'Try a different search term'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {filterUsers(following).map((user) => (
                    <UserCard key={user._id} user={user} showFollowButton={true} isFollowing={true} />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Connections;
